<template>
	<div v-if="event">
		<v-card :class="{'missing-audio': isMissingAudio}"
			rounded="6"
			variant="outlined"
			@contextmenu.prevent="isPlayAsset && openContextMenu($event)">

			<!-- Progress bar -->
			<div class="progress-bar"
				v-if="(isNowPlaying || isPaused) && !isSpotBlock && !isLocalBreak && !isDVTBlock"
				:style="{
						height: '50px',
						width: `${progressPercentage}%`,
						'border-bottom-right-radius': progressPercentage === 100 ? '6px' : 0
					}"
				:class="{
						'intro-playing': isIntroPlaying,
						'outro-playing': isOutroPlaying,
						'transition-none':isProgressPercentOlderMoreThanNew
					}">
			</div>

			<!-- Event row -->
			<div class="asset-container d-flex align-center px-2"
				:class="{
					'now-playing': isNowPlaying,
					'pb-3': (isSpotBlock || isLocalBreak || isDVTBlock) && (isNowPlaying || isPaused)
				}">

				<!-- Pot Number -->
				<v-tooltip location="bottom" :disabled="!potNumber" text="Pot Number">
					<template v-slot:activator="{ props }">
						<div v-bind="props" class="pot-number"
							:class="{
								'empty': !potNumber,
								'text-h6': potNumber > 9,
								'mt-3': (isSpotBlock || isLocalBreak || isDVTBlock) && (isNowPlaying || isPaused)
							}">
							{{ potNumber }}
						</div>
					</template>
				</v-tooltip>

				<!-- Start Time -->
				<v-tooltip location="bottom" text="Airtime">
					<template v-slot:activator="{ props }">
						<div v-bind="props" class="text-center airtime"
							:class="[isNowPlaying ? 'text-h2' : 'text-body-1']"
							:style="isMissingAudio ? 'color:white' : ''">
							{{ formattedAirDate }}
						</div>
					</template>
				</v-tooltip>

				<!-- Chain Type -->
				<div class="chain-type d-flex align-center justify-center">
					<div v-if="chainTypeIsStop && !isNowPlaying">
						<v-icon size="25" color="#C6002B">mdi-stop-circle</v-icon>
					</div>

					<div v-if="isPlayAsset && chainType">
						<v-menu close-on-content-click>
							<template v-slot:activator="{ props }">
								<v-btn v-bind="props" icon :disabled="chainTypeUpdating"
									variant="text" density="comfortable" size="30">
									<Image :img="chainTypeIcon" width="24" height="24"/>
								</v-btn>
							</template>
							<v-list>
								<v-list-item v-for="type in supportedChainTypes" :key="type.value" class="pl-2"
									@click="updateChainType(type.value)">
									<template v-slot:prepend>
										<v-avatar rounded="0" size="32px" class="ma-0">
											<Image :img="`${type.icon}.svg`" width="24" height="24"/>
										</v-avatar>
									</template>
									<v-list-item-title class="text-body-2"
										:class="{'font-weight-bold': event.Chain === type.value}">
										{{ type.description }}
									</v-list-item-title>
								</v-list-item>
							</v-list>
						</v-menu>
					</div>
				</div>

				<!-- Asset Type Icon -->
				<div v-if="isPlayAsset">
					<div v-if="event.AssetID">
						<v-tooltip location="bottom" v-if="isMissingAudio" text="Missing Audio">
							<template v-slot:activator="{ props }">
								<v-icon v-bind="props" icon="mdi-close-circle" class="asset-type-icon" size="24"/>
							</template>
						</v-tooltip>
						<v-menu v-if="!isLiveRead"
							v-model="songDetailsPopup.open"
							:open-on-hover="true"
							:close-on-content-click="false"
							content-class="song-details-popup">
							<template v-slot:activator="{ props }">
								<v-icon v-bind="props" :icon="assetTypeIcon" class="asset-type-icon" size="24"/>
							</template>
							<v-list density="compact" class="py-2">
								<v-expansion-panels @mouseleave="songDetailsPopup.open = false">
									<v-expansion-panel elevation="0">
										<v-expansion-panel-title
											hide-actions
											:ripple="false">
											<template v-slot:default="{ expanded }">
												<v-list-item>
													<template v-slot:prepend>
														<v-icon
															:icon="`mdi-menu-${expanded ? 'down' : 'right'}`"/>
													</template>
													<span> Event Details </span>
												</v-list-item>
											</template>
										</v-expansion-panel-title>
										<v-expansion-panel-text>
											<v-list-item v-for="(item, key) in eventDetails" :key="key">
												<v-list-item-subtitle v-if="typeof item !== 'object'">
													{{ key }}: {{ item }}
												</v-list-item-subtitle>

												<v-list-item-subtitle v-else>
													{{ key }}: {{ item.value }}
													<v-btn icon variant="text" density="compact"
														color="#717277"
														size="14" class="ml-2"
														@click="copyToClipboard(item.value)">
														<v-icon v-if="item.copyButton"
															icon="mdi-content-copy"
															size="14"/>
													</v-btn>
												</v-list-item-subtitle>
											</v-list-item>
										</v-expansion-panel-text>
									</v-expansion-panel>

									<v-expansion-panel elevation="0">
										<v-expansion-panel-title
											hide-actions
											:ripple="false">
											<template v-slot:default="{ expanded }">
												<v-list-item>
													<template v-slot:prepend>
														<v-icon
															:icon="`mdi-menu-${expanded ? 'down' : 'right'}`"/>
													</template>
													<span> Asset Details </span>
												</v-list-item>
											</template>
										</v-expansion-panel-title>
										<v-expansion-panel-text>
											<v-list-item v-for="(item, key) in assetDetails" :key="key">
												<v-list-item-subtitle v-if="typeof item !== 'object'">
													{{ key }}: {{ item }}
												</v-list-item-subtitle>

												<v-list-item-subtitle v-else>
													{{ key }}: {{ item.value }}
													<v-btn icon variant="text" density="compact"
														color="#717277"
														size="14" class="ml-2"
														@click="copyToClipboard(item.value)">
														<v-icon v-if="item.copyButton"
															icon="mdi-content-copy"
															size="14"/>
													</v-btn>
												</v-list-item-subtitle>
											</v-list-item>
										</v-expansion-panel-text>
									</v-expansion-panel>
								</v-expansion-panels>
							</v-list>
						</v-menu>
					</div>
					<div v-else>
						<v-icon icon="mdi-alert" class="asset-type-icon" size="24"/>
					</div>
				</div>
				<div v-else-if="isSpotBlock">
					<v-icon icon="mdi-currency-usd" class="asset-type-icon" size="24"/>
				</div>
				<div v-else-if="isDVTBlock">
					<v-icon icon="mdi-forum" class="asset-type-icon" size="24"/>
				</div>

				<!-- Event Info -->
				<div class="text-truncate">
					<!-- Asset -->
					<div v-if="isPlayAsset" :class="[isNowPlaying ? 'text-h1' : 'text-body-1']">
						<div v-if="event.AssetID" class="d-flex align-center">
							<!-- Info (Album / Title - Artist) -->
							<div class="d-flex flex-column text-truncate"
								:style="isMissingAudio ? 'color:white' : ''">
								<div class="text-truncate">
									<v-tooltip location="bottom" :text="`Title: ${event.Description ?? ''}`">
										<template v-slot:activator="{ props }">
											<span v-bind="props" :style="{'color': isMissingAudio ? 'white' : ''}">
												{{ event.Description }}
											</span>
										</template>
									</v-tooltip>
									-
									<v-tooltip location="bottom" :text="`Artist: ${event.Artist ?? ''}`">
										<template v-slot:activator="{ props }">
											<span v-bind="props">
												{{ event.Artist }}
											</span>
										</template>
									</v-tooltip>
								</div>
								<v-tooltip location="bottom" v-if="asset?.Album"
									:text="`Album: ${asset.Album ?? ''}`">
									<template v-slot:activator="{ props }">
										<span v-bind="props" class="text-caption text-truncate">
											{{ asset.Album }}
										</span>
									</template>
								</v-tooltip>
							</div>
						</div>
						<div v-else class="d-flex align-center">
							<v-icon icon="mdi-ear-hearing-off" class="asset-type-icon" size="20"/>
							<div>
								Unrecorded
							</div>
						</div>
					</div>

					<!-- Spot Block -->
					<div v-else-if="isSpotBlock" :class="[isNowPlaying ? 'text-h1' : 'text-body-1']">
						<span class="ml-4">{{ blockTitle ?? 'Spot Block' }}</span>
					</div>

					<!-- DVT Block -->
					<div v-else-if="isDVTBlock" :class="[isNowPlaying ? 'text-h1' : 'text-body-1']">
						<span class="ml-4">{{ blockTitle ?? 'DVT Block' }}</span>
					</div>
				</div>

				<!-- Missing Audio -->
				<div v-if="event.AssetID && isMissingAudio" class="no-audio text-body-1 ml-auto d-flex align-center">
					MISSING AUDIO
				</div>

				<!-- Duration -->
				<div
					v-if="(event && !isEmptyAudioNowPlaylist() && !isMissingAudio) || isSpotBlock || isLocalBreak || isDVTBlock"
					class="ml-auto d-flex align-center">
					<div class="text-center timer" :class="[isNowPlaying ? 'text-h2' : 'text-body-1']">
						<v-tooltip location="bottom" :text="durationTooltipText">
							<template v-slot:activator="{ props }">
								<span v-bind="props" :class="{'text-decoration-underline': isStretched || isSqueezed}">
									{{ secondsToFormat(duration, "HH:mm:ss", {trim: "left", stopTrim: "m"}) }}
								</span>
							</template>
						</v-tooltip>
					</div>

					<!-- Intro -->
					<div class="text-center timer"
						:class="{'text-h2': isNowPlaying, 'text-body-1': !isNowPlaying, 'd-none': lgAndDown}">
						<v-tooltip v-if="!(isSpotBlock || isLocalBreak || isDVTBlock)" location="bottom" text="Intro">
							<template v-slot:activator="{ props }">
								<span v-bind="props">
									{{
										isNowPlaying ?
										secondsToFormat(remainingIntro, "mm:ss") :
										secondsToFormat(intro, "mm:ss")
									}}
								</span>
							</template>
						</v-tooltip>
					</div>

					<!-- Progress/ Countdown -->
					<div class="text-center timer"
						:class="{'text-h2': isNowPlaying, 'text-body-1': !isNowPlaying, 'd-none': lgAndDown}"
						style="cursor: pointer;"
						@click.stop="toggleProgressCountdown()">
						<v-tooltip v-if="(isNowPlaying || isPaused)" location="bottom"
							:text="!progressCountdownEnabled ? 'Progress' : 'Countdown'">
							<template v-slot:activator="{ props }">
								<span v-bind="props">
									<span v-if="!progressCountdownEnabled">
										{{ secondsToFormat(elapsed, "HH:mm:ss", {trim: "left", stopTrim: "m"}) }}
									</span>
									<span v-else>
										{{ secondsToFormat(remainingTime, "HH:mm:ss", {trim: "left", stopTrim: "m"}) }}
									</span>
								</span>
							</template>
						</v-tooltip>
					</div>
				</div>

				<!-- Buttons -->
				<div class="d-flex justify-center action-buttons">
					<v-btn icon v-if="isNowPlaying && !isSequencerModeAuto"
						:disabled="stopOrPauseClicked"
						variant="text"
						density="compact"
						@click.stop.once="sequencerAction('Stop')">
						<v-icon size="20" icon="mdi-stop"/>
					</v-btn>
					<v-btn icon v-if="isNowPlaying && !isSequencerModeAuto"
						variant="text"
						density="compact"
						@click.stop.once="sequencerAction('Pause')" :disabled="stopOrPauseClicked">
						<v-icon size="20" icon="mdi-pause"/>
					</v-btn>
					<div v-if="!isNowPlaying">
						<v-btn icon v-if="!(isSpotBlock || isLocalBreak)"
							variant="text"
							density="compact"
							@click.stop.once="skipEvent({skip:true})">
							<v-icon size="20">mdi-eject</v-icon>
						</v-btn>
					</div>
				</div>

				<div id="context-menu-area"></div>
			</div>

			<div v-if="(isSpotBlock || isLocalBreak || isDVTBlock) && (isNowPlaying || isPaused)"
				class="spot-block-progress-container">
				<div v-for="spot in childPlaylistEvents" :key="spot.EventID"
					class="spot-block-content d-flex align-center"
					:style="{width: `${spotWidthPercentage(spot)}%`}">
					<div class="pot-number" :class="{ 'empty': !spot.PotNumber }">{{ spot.PotNumber }}</div>
					<v-tooltip location="bottom" :text="spot.Description">
						<template v-slot:activator="{ props }">
							<div v-bind="props" class="spot-progress-container">
								<div class="spot-progress-bar"
									:style="{width: `${spotProgressWidthPercentage(spot)}%`}">
								</div>
							</div>
						</template>
					</v-tooltip>
				</div>
			</div>

			<v-menu v-model="contextMenu.open" :activator="this.$el?.querySelector('#context-menu-area')"
				content-class="elevation-24" :target="[contextMenu.x, contextMenu.y]">
				<v-list>
					<v-tooltip location="bottom" :disabled="!!asset" text="Missing Audio">
						<template v-slot:activator="{ props }">
							<div v-bind="props">
								<v-list-item v-if="isPlayAsset" :disabled="!asset || auditionAudioMenu.open"
									@click="openAuditionAudioWidget($event, asset)">
									<v-list-item-title class="text-body-2">
										Audition Audio
									</v-list-item-title>
								</v-list-item>
							</div>
						</template>
					</v-tooltip>
				</v-list>
			</v-menu>

			<SkipFloatMenu v-if="skipSpotReasonMenu.open"
				:floatMenuModel="skipSpotReasonMenu"
				:floatMenuForm="skipSpotForm"
				:listTypes="skipTypes"
				@submitEvent="skipEvent"
				@close-menu="closeSkipSpotReasonMenu"/>

			<AuditionAudioWidget v-if="auditionAudioMenu.open && isPlayAsset && asset"
				:menu="auditionAudioMenu"
				:event="asset"
				parentComponent="now-playing"
				ref="auditionAudioWidget"
				@close-menu="closeAuditionAudioWidget"/>
		</v-card>
		<v-card variant="outlined" v-if="chainTypeIsStop">
			<v-row no-gutters class="stop-chain-type-warning-line justify-end"
				:class="{'chain-stop-playing': isOutroPlaying}">
				<div class="d-flex align-center">
					<div class="text-caption">
						{{
						isNowPlaying || nowPlayingItems ? "Audio will stop after this event." :
						"Audio stopped after this event."
						}}
					</div>
					<div class="d-inline-flex px-4">
						<v-icon size="16" color="#C6002B">mdi-stop-circle</v-icon>
					</div>
				</div>
			</v-row>
		</v-card>
	</div>
</template>

<script>
/* Libraries */
import * as _ from "lodash";
import moment from "moment-timezone";

/* Modules */
import AuditionAudioWidget from "@/components/AuditionAudioWidget.vue";
import Image from "@/components/Image.vue";
import SkipFloatMenu from "@/components/SkipFloatMenu.vue";

/* Enums */
import EventStatus from "@/enums/EventStatus";
import EventTypes from "@/enums/EventTypes";
import BedTypes from "@/enums/BedTypes";
import POITypes from "@/enums/POITypes";

/* Mixins */
import AssetProperties from "@/mixins/AssetProperties";
import AuditionAudioMixin from "@/mixins/AuditionAudio";
import DurationFormatMixin from "@/mixins/DurationFormatMixin";
import EventProperties from "@/mixins/EventProperties";

export default {
	name: "OnAirEvent",

	components: {
		AuditionAudioWidget,
		Image,
		SkipFloatMenu
	},

	mixins: [
		AssetProperties,
		AuditionAudioMixin,
		DurationFormatMixin,
		EventProperties
	],

	props: ["selectedStation", "event", "sequencerItems", "listChainStop"],

	data() {
		return {
			elapsed: null,
			remainingTime: null,
			remainingIntro: null,
			isIntroPlaying: false,
			isOutroPlaying: false,
			progressPercentage: null,
			asset: {},
			isProgressPercentOlderMoreThanNew: false
		}
	},

	computed: {
		nowPlayingItems() {
			return this.sequencerItems.filter(({StatusCode}) => StatusCode === EventStatus.Current);
		},

		isStretched() {
			return this.event.PitchlessPlayRate && this.event.PitchlessPlayRate > 0;
		},

		stretchedValue() {
			return this.event.PitchlessPlayRate;
		},

		isSqueezed() {
			return this.event.PitchedPlayRate && this.event.PitchedPlayRate > 0;
		},

		squeezedValue() {
			return this.event.PitchedPlayRate;
		},

		durationTooltipText() {
			if (this.isSqueezed) {
				return `Duration - Squeezed by: ${parseFloat(((this.squeezedValue - 1) * 100).toFixed(2))}%`
			}
			else if (this.isStretched) {
				return `Duration - Stretched by: ${parseFloat(((this.stretchedValue - 1) * 100).toFixed(2))}%`
			}
			else {
				return "Duration";
			}
		},

		formattedAirDate() {
			return moment.tz(this.event.StartDateTime, this.sequencer.SequencerTimeZone)
			             .format("HH:mm:ss");
		},

		duration() {
			if (this.isPlayAsset) {
				return this.event.DurationToSegue ?? 0;
			}
			else {
				const events = this.$store.getters.getPlaylistById(this.childPlaylistEvents[0]?.PlaylistID)?.Events;
				if (events?.some(e => e.Asset?.BedType === BedTypes.Bed)) {
					// Split events into Ready and Invalid
					const [bedReadyEvents, bedInvalidEvents] = _.partition(
						events,
						e => e.Asset?.BedType === BedTypes.Bed
					);

					// Calculate Ready Duration
					const bedReadyDuration = bedReadyEvents.reduce((total, e) => {
						const asset = e.Asset;
						const poiMarkupOverride = this.$store.getters.getAssetPOIMarkupOverride(asset?.AssetID);
						const markers = (poiMarkupOverride ?? asset?.POIMarkup)?.POIMarkers || [];

						// Prioritize EarlySegue marker, fallback to PlaybackView, then Length
						const earlySegueMarker = markers.find(p => p.POIType === POITypes.EarlySegue);
						const duration = earlySegueMarker?.Position ?? 0;

						return total + duration;
					}, 0);

					// Calculate Invalid Duration and combine
					const bedInvalidDuration = this.playlistSpotBlockDuration(bedInvalidEvents);
					return bedReadyDuration + bedInvalidDuration;
				}
				else {
					return this.childPlaylistEvents?.reduce((duration, e) => duration + (e.DurationToSegue ?? 0), 0);
				}
			}
		},

		blockTitle() {
			// Display the title of the first Ready, Now playing or Paused spot, or the one of the last played
			return this.event.ChildItems?.find(i => [
					   EventStatus.Ready, EventStatus.Current, EventStatus.Paused
				   ].includes(i.StatusCode))?.Description ??
			       this.event.ChildItems?.findLast(i => i.StatusCode === EventStatus.Played)?.Description;
		},

		potNumber() {
			// in case of spotblock show the pot number of the first Ready, Now playing or Paused spot, or the one of the last played
			return (this.isSpotBlock || this.isLocalBreak || this.isDVTBlock) ?
			       (this.event.ChildItems?.find(i => [
						EventStatus.Ready, EventStatus.Current, EventStatus.Paused
					].includes(i.StatusCode))?.PotNumber ??
			        this.event.ChildItems?.findLast(i => i.StatusCode === EventStatus.Played)?.PotNumber) :
			       this.event.PotNumber;
		},

		// Overrides AssetProperties mixin's computed variable
		intro() {
			return this.event?.MaxIntro;
		},

		childPlaylistEvents() {
			let items = [];
			if (this.isSpotBlock || this.isDVTBlock) {
				items = this.event.ChildItems?.filter(
					e => !e.Skipped &&
					     [EventTypes.PlayAsset, EventTypes.SpotBlock, EventTypes.DVTBlock].includes(e.EventType)
				) ?? [];
			}
			else if (this.isLocalBreak) {
				this.event.ChildItems.forEach(i => {
					if (i.EventType === EventTypes.PlayAsset) {
						items.push(i)
					}
					else if (i.EventType === EventTypes.SpotBlock) {
						items.push(...(i?.ChildItems?.filter(
							e => !e.Skipped && [EventTypes.PlayAsset, EventTypes.SpotBlock].includes(e.EventType)
						) ?? []));
					}
				})
			}
			return items;
		},

		playingChildPosition() {
			return this.event?.PlayingChildPosition ?? 0;
		},

		spotsLength() {
			return this.event?.ChildItems?.length ?? 0;
		},

		isMissingAudio() {
			return this.isPlayAsset && !_.isEmpty(this.asset) && this.asset && !this.asset?.POIMarkup?.Path;
		},

		lgAndDown() {
			return this.$vuetify.display.lgAndDown;
		}
	},

	methods: {
		async loadAsset() {
			this.asset = await this.$store.dispatch("getAsset", this.event.AssetID);
		},

		playlistSpotBlockDuration(childPlaylistEvents) {
			const events = childPlaylistEvents.filter(
				e => !e.Skipped && [
					EventTypes.PlayAsset,
					EventTypes.SpotBlock,
					EventTypes.DVTBlock,
					EventTypes.Comment
				].includes(e.EventType)
			);

			return events.reduce((acc, e) => acc + (e.Asset?.POIMarkup?.Length ?? 0), 0);
		},

		setColors() {
			let eventTypeColor = "rgb(169, 175, 178)";
			let nowPlayingLabelColor = eventTypeColor;

			if (this.isPlayAsset) {
				if (!this.isEmptyAudioNowPlaylist() && !!this.assetType?.Color) {
					eventTypeColor = this.assetType?.Color;
				}
				nowPlayingLabelColor = eventTypeColor;
			}
			else if (this.isSpotBlock) {
				eventTypeColor = !this.darkMode ? "rgb(2, 140, 60)" : "rgb(49, 234, 65)";
				nowPlayingLabelColor = !this.darkMode ? "rgb(2, 140, 60)" : "rgb(45,159,57)";
			}
			else if (this.isLocalBreak) {
				eventTypeColor = "rgb(63, 81, 166)";	//todo
				nowPlayingLabelColor = "rgb(63, 81, 166)";
			}
			else if (this.isDVTBlock) {
				//todo: pending change: need to specify the correct colors
				eventTypeColor = !this.darkMode ? "rgb(10,11,9)" : "rgb(252,131,62)";
				nowPlayingLabelColor = eventTypeColor;
			}

			this.$el.style.setProperty("--eventTypeColorRGBValues", eventTypeColor.match(/\d+/g).join(","));		// Get only the rgb values
			this.$el.style.setProperty("--nowPlayingLabelColor", nowPlayingLabelColor.match(/\d+/g).join(","));	// Get only the rgb values
		},

		isEmptyAudioNowPlaylist() {
			return this.event.DurationToFadeOut === 0 &&
			       this.event.DurationToSegue === 0 &&
			       this.intro === 0;
		},

		spotWidthPercentage(spot) {
			return parseInt(spot.DurationToSegue) / parseInt(this.duration) * 100;
		},

		elapsedTimeFromChildItems(childItems) {
			let elapsed = 0;
			const items = childItems?.filter(
				e => !e.Skipped && [EventTypes.PlayAsset, EventTypes.SpotBlock].includes(e.EventType)
			) ?? [];

			items.forEach(i => {
				if (i.ChildItems) {
					elapsed += this.elapsedTimeFromChildItems(i.ChildItems);
				}
				else {
					if ([EventStatus.Current, EventStatus.Paused].includes(i.StatusCode)) {
						elapsed += i.Elapsed;
					}
					else if ([EventStatus.Played, EventStatus.PendingPlayed].includes(i.StatusCode)) {
						elapsed += i.DurationToSegue;
					}
				}
			});

			return elapsed;
		},

		setProgressInterval() {
			const sequencerEvent = this.$store.getters.getSequencerItemByEventID(
				this.selectedStation.StationID,
				this.event.EventID
			);
			if (!(this.isSpotBlock || this.isLocalBreak)) {
				this.elapsed = sequencerEvent?.Elapsed;
			}
			else {
				this.elapsed = this.elapsedTimeFromChildItems(sequencerEvent?.ChildItems ?? []);
			}

			if (!_.isNil(this.elapsed)) {
				this.workerIntervalId = this.$worker.setInterval(this.progressInterval, 1000);
			}
		},

		progressInterval() {
			const sequencerEvent = this.$store.getters.getSequencerItemByEventID(
				this.selectedStation.StationID,
				this.event.EventID
			);
			if (!this.isPaused) {
				this.elapsed++;
			}

			this.isIntroPlaying = this.intro && (this.intro + 1) >= this.elapsed;

			const progressPercentage = (this.elapsed / (this.isIntroPlaying ? this.intro : this.duration)) * 100;
			this.progressPercentage = progressPercentage < 100 ? progressPercentage : 100;

			this.remainingTime = this.duration >= this.elapsed ? (this.duration - this.elapsed) : 0;
			this.remainingIntro = this.elapsed && (this.intro + 1) >= this.elapsed ?
			                      (this.intro + 1 - this.elapsed) : this.intro;
			const outroTimeframeInSecs = 10;    // 10 last seconds of the audio
			this.isOutroPlaying = this.remainingTime <= outroTimeframeInSecs;

			// Update sequencer item
			this.$store.commit(
				"setSequencerItemByEventID",
				{
					stationID: this.selectedStation.StationID,
					item: {
						...sequencerEvent,
						Elapsed: this.elapsed,
						RemainingTime: this.remainingTime,
						RemainingIntro: this.remainingIntro
					}
				}
			);

			// Update the spot in order to update the elapsed time (progress bar) and the countdown
			if (this.isSpotBlock || this.isLocalBreak) {
				const nowPlayingSpot = this.childPlaylistEvents.find(c => c.StatusCode === EventStatus.Current);
				if (nowPlayingSpot) {
					const sequencerSpotEvent = this.$store.getters.getSequencerItemByEventID(
						this.selectedStation.StationID,
						nowPlayingSpot.EventID
					);

					const spotDuration = sequencerSpotEvent?.DurationToSegue ?? 0;
					const spotElapsed = sequencerSpotEvent?.Elapsed + 1;
					const spotIntro = sequencerSpotEvent?.MaxIntro ?? 0;
					const spotRemainingTime = spotDuration >= spotElapsed ? (spotDuration - spotElapsed) : 0;
					const spotRemainingIntro = spotElapsed && (spotIntro + 1) >= spotElapsed ?
					                           (spotIntro + 1 - spotElapsed) : spotIntro;

					// Update sequencer spot item, to properly get the Elapsed value for the spots progress bars
					this.$store.commit(
						"setSequencerItemByEventID",
						{
							stationID: this.selectedStation.StationID,
							item: {
								...sequencerSpotEvent,
								Elapsed: spotElapsed,
								RemainingTime: spotRemainingTime,
								RemainingIntro: spotRemainingIntro
							}
						}
					);

					// In case of local break
					if (this.isLocalBreak) {
						const nowPlayingChild = this.event.ChildItems.find(c => c.StatusCode === EventStatus.Current);

						// Check if the current playing item is a spotblock,
						// so we have to update the times of the spot block child item too.
						if (nowPlayingChild.EventType === EventTypes.SpotBlock) {
							// Get the spot block child item as it's stored in the sequencerEventsMap
							const sbChildEvent = this.$store.getters.getSequencerItemByEventID(
								this.selectedStation.StationID,
								nowPlayingChild.EventID
							);

							let sbDuration = 0;	// The duration of the spot block child item
							let sbElapsed = 0;	// The elapsed time of the spot block child item
							sbChildEvent.ChildItems?.forEach(c => {
								if (!c.Skipped && c.EventType === EventTypes.PlayAsset) {
									sbDuration += c?.DurationToSegue ?? 0;
									sbElapsed += this.$store.getters.getSequencerItemByEventID(
										this.selectedStation.StationID,
										c.EventID
									).Elapsed ?? c.Elapsed;
								}
							});

							const sbIntro = sbChildEvent?.MaxIntro ?? 0;
							const sbRemainingTime = sbDuration >= sbElapsed ? (sbDuration - sbElapsed) : 0;
							const sbRemainingIntro = sbElapsed && (sbIntro + 1) >= sbElapsed ?
							                         (sbIntro + 1 - sbElapsed) : sbIntro;

							// Update sequencer spot block child item, to properly get the Elapsed value for the spots progress bars
							this.$store.commit(
								"setSequencerItemByEventID",
								{
									stationID: this.selectedStation.StationID,
									item: {
										...sbChildEvent,
										Elapsed: sbElapsed,
										RemainingTime: sbRemainingTime,
										RemainingIntro: sbRemainingIntro
									}
								}
							);

						}
					}
				}
			}

			// if the event is Paused get only the progress and clear the interval
			if (this.isPaused) {
				this.clearProgressInterval();
			}
		},

		clearProgressInterval() {
			this.$worker.clearInterval(this.workerIntervalId);
		},

		spotProgressWidthPercentage(spot) {
			const sequencerSpotEvent = this.$store.getters.getSequencerItemByEventID(
				this.selectedStation.StationID,
				spot.EventID
			);
			let progress = 0;
			switch (sequencerSpotEvent.StatusCode) {
				case EventStatus.Current:
				case EventStatus.Paused:
					progress = sequencerSpotEvent.Elapsed / sequencerSpotEvent.DurationToSegue * 100;
					break;
				case EventStatus.Played:
				case EventStatus.PendingPlayed:
					progress = 100;
					break;
			}
			return progress < 100 ? progress : 100;
		},

		copyToClipboard(textToCopy) {
			navigator.clipboard.writeText(textToCopy)
			         .then(() => {
						 this.$store.commit("SHOW_SUCCESS_NOTIFICATION", {
							 message: "ID copied successfully!"
						 });
					 })
		},

		sequencerAction(action) {
			this.$store.dispatch("sequencerActionRequest", {action: action, msg: {EventID: this.event.EventID}});
		}
	},

	watch: {
		sequencer(snap, previousSnap) {
			if (this.isNowPlaying &&
			    snap?.StartLiveSequencerSnapshot?.StartLiveState !==
			    previousSnap?.StartLiveSequencerSnapshot?.StartLiveState) {
				this.clearProgressInterval();
				this.setProgressInterval();
			}
		},

		isNowPlaying(isNowPlaying) {
			if (isNowPlaying) {
				this.clearProgressInterval();
				this.setProgressInterval();
			}
			else {
				this.clearProgressInterval();
			}
		},

		playingChildPosition(current, previous) {
			if (this.isNowPlaying && current !== previous) {
				this.clearProgressInterval();
				this.setProgressInterval();
			}
		},

		spotsLength(current, previous) {
			if (this.isNowPlaying && current !== previous) {
				this.clearProgressInterval();
				this.setProgressInterval();
			}
		},

		progressPercentage(value, oldValue) {
			// When transitioning from intro playing to main audio playing, remove the transition so the progress bar starts over
			this.isProgressPercentOlderMoreThanNew = oldValue > value;
		}
	},

	mounted() {
		if (this.isPlayAsset && _.isEmpty(this.asset)) {
			this.loadAsset();
		}

		if (this.isNowPlaying || this.isPaused) {
			this.setProgressInterval();
		}
	},

	beforeUnmount() {
		this.clearProgressInterval();
		this.$store.commit(
			"deleteSequencerItemByEventID",
			{stationID: this.selectedStation.StationID, item: this.event.EventID}
		);
	}
};
</script>

<style lang="scss" scoped>
.v-card:not(.skip-spot-block-reason-container) {
	background-color: rgba(var(--eventTypeColorRGBValues), .07);
	border: 1px solid rgb(var(--eventTypeColorRGBValues));
	margin-bottom: 2px;
	position: relative;

	.progress-bar {
		background-color: rgba(var(--eventTypeColorRGBValues), .15);
		border-top-right-radius: 0 !important;
		border-top-left-radius: 0 !important;
		border-bottom-left-radius: inherit;
		position: absolute;
		transition: width 1.5s linear, background-color .5s ease;

		&.intro-playing {
			background-color: rgba(255, 230, 87, .64);
		}

		&.outro-playing {
			background-color: rgba(226, 44, 58, .64);
		}

		&.transition-none {
			transition: none !important;
		}
	}

	@mixin pot-number {
		background-color: #ffffff;
		border: 1px solid rgb(var(--eventTypeColorRGBValues));
		border-radius: 2px;
		color: $grey-400;
		font-size: 24px;
		font-weight: 700;
		min-width: 30px;
		padding: 1px 5px;
		text-align: center;

		&.empty {
			background: transparent;
			border-color: transparent !important;
		}

		@content;

		@media only screen and (max-width: 1920px) {
			padding: 1px 2px;
		}
	}

	.asset-container {
		height: 50px;
		position: relative;

		.pot-number {
			@include pot-number;
		}

		.airtime {
			min-width: 100px;
		}

		.chain-type {
			min-width: 60px;
		}

		.asset-type-icon {
			color: rgb(var(--eventTypeColorRGBValues));
			margin-right: 8px;
			min-width: 30px;
		}

		.timer {
			min-width: 60px;
		}

		.no-audio {
			color: white;
			min-width: 120px;
		}

		.action-buttons {
			min-width: 60px;

			.v-btn {
				min-width: 30px;
			}
		}
	}

	.spot-block-progress-container {
		bottom: 1px;
		display: flex;
		padding-left: 70px;
		padding-right: 32px;
		position: absolute;
		width: 100%;

		.spot-block-content {
			min-width: 4%;

			.pot-number {
				@include pot-number {
					font-size: 11px;
					margin: 0 2px 1px 6px;
					min-width: 14px;
					padding: 0;
				}
			}

			&:first-child {
				.pot-number {
					margin-left: 0 !important;
				}
			}

			.spot-progress-container {
				background: $grey-300;
				border-radius: 10px;
				margin: 0 2px 1px 2px;
				height: 8px;
				width: calc(100% - 15px);

				.spot-progress-bar {
					background: rgb(var(--eventTypeColorRGBValues));
					border-radius: 10px;
					height: 100%;
				}
			}
		}
	}

	.stop-chain-type-warning-line {
		height: 20px;
		background: $ihm-grey1;

		div {
			letter-spacing: .5px !important;
		}
	}

	&.missing-audio {
		background: #484C4F !important;
		border: 1px dashed #2D2D2D;

		.v-icon:not(.mdi-alert, .mdi-eject, .mdi-information-outline, .asset-type-icon, .mdi-cancel) {
			color: #E22100 !important;
			background: radial-gradient(white 50%, transparent 50%);
		}

		.v-icon:not(:not(.mdi-information-outline, .asset-type-icon, .mdi-cancel, .mdi-alert)) {
			color: white !important;
		}

		:hover {
			.v-icon:not(.mdi-close-circle,.mdi-play,.mdi-stop,.mdi-pause,.asset-type-icon) {
				color: white !important;
			}
		}
	}

	&.v-theme--dark {
		background-color: transparent;

		.progress-bar {
			background-color: rgba(var(--eventTypeColorRGBValues), .61);

			&.intro-playing {
				background-color: rgba(255, 230, 87, .64);
			}

			&.outro-playing {
				background-color: rgba(226, 44, 58, .64);
			}
		}

		.asset-container {
			color: rgb(var(--eventTypeColorRGBValues));

			.pot-number {
				background-color: transparent;
				color: $grey-300;
			}

			.v-icon.mdi-information-outline {
				color: rgb(var(--eventTypeColorRGBValues));
			}

			.action-buttons .v-icon {
				color: $grey-300;
			}

			&.now-playing {
				color: $ihm-white;

				.pot-number {
					background-color: $grey-600;
					color: $ihm-white;
				}

				.v-image {
					filter: invert(100%) sepia(100%) saturate(0%) brightness(300%);
				}

				.v-icon.mdi-information-outline,
				.action-buttons .v-icon {
					color: $ihm-white;
				}
			}
		}

		.spot-block-progress-container .spot-block-content .spot-progress-container {
			background: #d9d9d9;
		}
	}
}

.chain-stop-playing {
	background: rgba(226, 33, 0, 0.3) !important;
	border: 2px solid rgba(226, 33, 0, 0.1) !important;
}
</style>
